import { createSelector } from '@ngrx/store';
import { normalizeRTTimestamp, VirtualRTStateType } from 'ngx-core-services';

import { BlockState, RealTimeTelemetryUiState } from '../state/block.state';
import { selectBlockState } from './block.selectors';
import { selectSystemVirtualRTStatusBySystemId } from './system.selectors';

export interface RealTimeTelemetrySessionState extends RealTimeTelemetryUiState {
  active: boolean;
  duration: number;
  overlayHidden: boolean;
  overlayRequestedByUser: boolean;
  startTS: number | null;
}

/** Shared projection: merge virtualRTStatus + rtMeta into a unified session state. */
const mapToRTSession = (
  virtualRTStatus: VirtualRTStateType | null | undefined,
  rtMeta: RealTimeTelemetryUiState | null | undefined,
): RealTimeTelemetrySessionState => {
  const startTS = normalizeRTTimestamp(rtMeta?.startTS ?? virtualRTStatus?.startTS);
  const runtimeActive = Boolean(rtMeta?.active);
  const backendActive = virtualRTStatus?.active;

  return {
    ...rtMeta,
    // UI active state follows runtime session; backend status can only force deactivation.
    active: backendActive === false ? false : runtimeActive,
    duration: Number(rtMeta?.duration ?? virtualRTStatus?.duration ?? 0),
    overlayHidden: Boolean(rtMeta?.overlayHidden),
    overlayRequestedByUser: Boolean(rtMeta?.overlayRequestedByUser),
    startTS,
    stopping: rtMeta?.stopping ?? false,
  };
};

export const selectRealTimeTelemetryComponent = createSelector(
  selectBlockState,
  (state: BlockState) => state.blockList.RealTimeTelemetryComponent
);

export const selectRealTimeTelemetryMetaBySystemId = (systemId: string) => createSelector(
  selectRealTimeTelemetryComponent,
  (state) => state?.[systemId] ?? null
);

export const selectRealTimeTelemetryRuntimeActiveBySystemId = (systemId: string) => createSelector(
  selectRealTimeTelemetryMetaBySystemId(systemId),
  (rtMeta) => Boolean(rtMeta?.active),
);

export const selectRealTimeTelemetrySessionBySystemId = (systemId: string) => createSelector(
  selectSystemVirtualRTStatusBySystemId(systemId),
  selectRealTimeTelemetryMetaBySystemId(systemId),
  (virtualRTStatus, rtMeta): RealTimeTelemetrySessionState => mapToRTSession(virtualRTStatus, rtMeta),
);
